/**
 * TabBar - Horizontal tab navigation bar with semantic tokens and sticky support
 *
 * Use this for horizontal tab navigation (e.g., page section tabs).
 * For vertical sidebar-style tabs, use the regular Tabs component.
 */
import { ComponentProps } from "react";
import ChakraTabList from "@ui-kit/disclosure/TabList/TabList.web";
import ChakraTab from "@ui-kit/disclosure/Tab/Tab.web";
import BaseTabs from "@ui-kit/disclosure/BaseTabs/BaseTabs.web";

// Re-export the main Tabs component from BaseTabs
export const TabBar = BaseTabs;

type TabBarListProps = ComponentProps<typeof ChakraTabList> & {
  bgColor?: string;
  border?: string;
};

export function TabBarList({
  bgColor,
  border,
  ...props
}: TabBarListProps): JSX.Element {
  return (
    <ChakraTabList
      zIndex="sticky"
      backgroundColor={bgColor ?? "bg"}
      borderBottom={border ?? "1px solid"}
      borderColor="border"
      paddingTop="sm"
      width="100%"
      height="element.4xl"
      outline="1px solid transparent"
      {...props}
    />
  );
}

type TabBarItemProps = ComponentProps<typeof ChakraTab>;

export function TabBarItem(props: TabBarItemProps): JSX.Element {
  return (
    <ChakraTab
      borderRadius="md"
      borderBottom="none"
      padding="none"
      // TODO: Create Tab variant with custom indicator styling
      // eslint-disable-next-line showpass-custom/prefer-semantic-props
      css={{
        "&::before": {
          display: "none !important",
        },
        "& span": {
          paddingBottom: "sm",
          fontSize: "sm",
          borderBottom: "1px solid var(--chakra-colors-border)",
        },
        // Support both data-selected (v3 tabs) and data-state for compatibility
        "&[data-selected] span, &[data-state=active] span": {
          color: "var(--chakra-colors-primary-700)",
          borderBottom: "2px solid var(--chakra-colors-primary-700)",
        },
      }}
      {...props}
    />
  );
}

export default TabBar;
